"use client";

import { ErrorBoundary } from "@/components/error/error-boundary";
import { ServerProductCard } from "@/components/features/listings/ServerProductCard";
import { SaveListingButton } from "@/components/features/listings/save-listing-button";
import { quoteHrefFor } from "@/components/listing/browse/card-grid";
import type { DiscoveryProduct } from "@/components/discovery/types";
import { ListingsClusterMapClient } from "@/components/location/ListingsClusterMapClient";
import { previewViewport, useClusterListings } from "@/components/listing/browse/listing-map-pane";
import { NEAR_YOU_REACH_MILES, useNearYouProducts } from "@/components/home/sections/near-you/use-near-you-products";
import { useVisitorLocation } from "@/components/home/sections/near-you/use-visitor-location";
import { US_MAP_CENTER } from "@/lib/maps/map-defaults";
import { LANDING_CARD_SIZES } from "@/components/features/listings/image-sizes";
import type { HeroLocation } from "@/components/home/context/LocationContext";
import { cn } from "@/lib/utils";
import { LandingCardRow } from "./landing-card-row";
import { LandingSectionHead } from "./landing-section-head";

const NATIONWIDE_ZOOM = 3;
const SKELETON_CARDS = 5;

const MAP_FRAME_CLASSES = "h-[180px] w-full shrink-0 lg:h-[411px] lg:w-[425px]";

function NearYouMap({ products, isLoading }: { products: DiscoveryProduct[]; isLoading: boolean }) {
  const listings = useClusterListings(products);
  const viewport = previewViewport(listings);
  if (isLoading) {
    return <div aria-hidden className={cn(MAP_FRAME_CLASSES, "animate-pulse rounded-xl bg-skeleton")} />;
  }
  return (
    <ListingsClusterMapClient
      listings={listings}
      className={MAP_FRAME_CLASSES}
      mapClassName="h-full w-full rounded-xl border-0"
      defaultZoom={viewport?.zoom ?? NATIONWIDE_ZOOM}
      defaultCenter={viewport?.center ?? { latitude: US_MAP_CENTER.lat, longitude: US_MAP_CENTER.lng }}
      emptyMessage="No listings near you yet."
    />
  );
}

function NearYouCards({ products, isLoading }: { products: DiscoveryProduct[]; isLoading: boolean }) {
  if (isLoading) {
    return (
      <LandingCardRow>
        {Array.from({ length: SKELETON_CARDS }, (_, index) => (
          <div key={index} aria-hidden className="h-[380px] animate-pulse rounded-xl bg-skeleton" />
        ))}
      </LandingCardRow>
    );
  }
  if (products.length === 0) {
    return <p className="text-[15px] text-fg-muted lg:self-center">No listings near you yet.</p>;
  }
  return (
    <LandingCardRow>
      {products.map((product) => (
        <ServerProductCard
          key={product._id}
          item={{
            ...product,
            quantityAvailable: product.availableToSell,
            productTypeName: product.productType?.displayName,
            distanceMiles: product.distance,
          }}
          sizes={LANDING_CARD_SIZES}
          quoteHref={quoteHrefFor(product)}
          saveSlot={
            <SaveListingButton
              productId={product._id}
              listingTitle={product.title}
              listingImageUrl={product.primaryImageUrl ?? undefined}
              presentation="overlay"
            />
          }
        />
      ))}
    </LandingCardRow>
  );
}

function browseHref(location: HeroLocation | null): string {
  if (!location) return "/listing";
  const params = new URLSearchParams({ lat: String(location.lat), lng: String(location.lng) });
  if (location.postalCode) params.set("zip", location.postalCode);
  if (location.city) params.set("city", location.city);
  if (location.state) params.set("state", location.state);
  params.set("radius", String(NEAR_YOU_REACH_MILES));
  return `/listing?${params.toString()}`;
}

function NearYouBody() {
  const { location, isSeeding } = useVisitorLocation();
  const { products, isLoading } = useNearYouProducts({ location, isSeeding });
  return (
    <>
      <LandingSectionHead
        id="landing-near-you"
        heading="Listings near you"
        action={{ href: browseHref(location), label: "Browse all listings", shortLabel: "Browse all" }}
      />
      <div className="flex flex-col gap-[15px] lg:flex-row">
        <NearYouMap products={products} isLoading={isLoading} />
        <NearYouCards products={products} isLoading={isLoading} />
      </div>
    </>
  );
}

export function LandingNearYou() {
  return (
    <ErrorBoundary
      label="home-near-you"
      fallback={
        <>
          <LandingSectionHead
            id="landing-near-you"
            heading="Listings near you"
            action={{ href: "/listing", label: "Browse all listings", shortLabel: "Browse all" }}
          />
          <p className="text-[15px] text-fg-muted">Listings near you are unavailable right now.</p>
        </>
      }
    >
      <NearYouBody />
    </ErrorBoundary>
  );
}
